import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatTabsModule } from '@angular/material/tabs';
import { Router } from '@angular/router';
import { BasicDialogService } from '@principle-theorem/ng-shared';
import { Firestore } from '@principle-theorem/shared';
import { PhoneMultiFactorInfo } from 'firebase/auth';
import { MULTIFACTOR_CURRENT_USER_DOC } from '../multifactor-current-user-doc.token';
import { MultifactorService } from '../multifactor.service';
import {
  IMultifactorEnrolmentResult,
  MultifactorFactorId,
  MultifactorMethod,
} from '../multifactor.types';
import { PhoneEnrolComponent } from './phone-enrol.component';
import { TotpEnrolComponent } from './totp-enrol.component';

@Component({
  selector: 'pt-multifactor-enrol-page',
  templateUrl: './multifactor-enrol-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatCardModule,
    MatButtonModule,
    MatTabsModule,
    PhoneEnrolComponent,
    TotpEnrolComponent,
  ],
})
export class MultifactorEnrolPageComponent {
  private _router = inject(Router);
  private _multifactorService = inject(MultifactorService);
  private _basicDialog = inject(BasicDialogService);
  private _currentUser = toSignal(inject(MULTIFACTOR_CURRENT_USER_DOC));

  userPhone = computed(() => this._currentUser()?.phone);

  async onEnrolled(result: IMultifactorEnrolmentResult): Promise<void> {
    if (result.success) {
      try {
        await this._syncVerifiedPhoneToUser(result);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to sync phone to user profile:', error);
      }
      await this._router.navigate(['/']);
    }
  }

  private async _syncVerifiedPhoneToUser(
    result: IMultifactorEnrolmentResult
  ): Promise<void> {
    if (result.method !== MultifactorMethod.Phone) {
      return;
    }

    const user = this._currentUser();
    const verifiedPhoneNumber = this._getVerifiedPhoneNumber();

    if (!user || !verifiedPhoneNumber || user.phone === verifiedPhoneNumber) {
      return;
    }

    const confirmed = await this._basicDialog.confirm({
      title: 'Update profile mobile number?',
      prompt: `Update your profile mobile number to match the verified phone number: ${verifiedPhoneNumber}?`,
    });

    if (confirmed) {
      await Firestore.patchDoc(user.ref, { phone: verifiedPhoneNumber });
    }
  }

  private _getVerifiedPhoneNumber(): string | undefined {
    const phoneFactor = this._multifactorService
      .getEnrolledFactors()
      .find((factor) => factor.factorId === MultifactorFactorId.Phone);

    if (!phoneFactor || !('phoneNumber' in phoneFactor)) {
      return;
    }

    return (phoneFactor as PhoneMultiFactorInfo).phoneNumber;
  }
}
